import { trackRouteTiming } from './analytics';

/**
 * Per-navigation timing record. One entry exists at a time, keyed by path.
 * We deliberately use a module-level singleton because navigations are
 * sequential within a SPA tab and we want FallbackTimer + AnalyticsTracker
 * to coordinate without prop drilling or React context.
 */
type RouteTimingRecord = {
  path: string;
  startedAt: number;
  suspenseStartedAt: number | null;
  suspenseDurationMs: number;
  prefetched: boolean;
  cacheHit: boolean;
  finished: boolean;
};

let current: RouteTimingRecord | null = null;

const now = (): number =>
  typeof performance !== 'undefined' ? performance.now() : Date.now();

/** Begin a new navigation timer. Replaces any in-flight unfinished record. */
export function startRouteTiming(
  path: string,
  prefetched: boolean,
  cacheHit: boolean,
): void {
  current = {
    path,
    startedAt: now(),
    suspenseStartedAt: null,
    suspenseDurationMs: 0,
    prefetched,
    cacheHit,
    finished: false,
  };
}

/**
 * Returns the `cacheHit` flag captured at navigation start for `path`,
 * or null if no active record matches. FallbackTimer reads this so the
 * `route_fallback` event reflects pre-navigation cache state, not a
 * stale post-load query.
 */
export function getActiveCacheHit(path: string): boolean | null {
  if (!current || current.path !== path) return null;
  return current.cacheHit;
}

/** Called when a Suspense fallback first paints for the active navigation. */
export function markSuspenseStart(path: string): void {
  if (!current || current.path !== path || current.finished) return;
  if (current.suspenseStartedAt !== null) return;
  current.suspenseStartedAt = now();
}

/** Called when a Suspense fallback unmounts (chunk resolved). */
export function markSuspenseEnd(path: string): void {
  if (!current || current.path !== path || current.finished) return;
  if (current.suspenseStartedAt === null) return;
  current.suspenseDurationMs += now() - current.suspenseStartedAt;
  current.suspenseStartedAt = null;
}

/**
 * Marks the navigation as fully rendered and emits the consolidated event.
 * Safe to call multiple times — only the first call wins.
 */
export function finishRouteTiming(path: string): void {
  if (!current || current.path !== path || current.finished) return;
  // If a Suspense window is still open at finish, close it now.
  if (current.suspenseStartedAt !== null) {
    current.suspenseDurationMs += now() - current.suspenseStartedAt;
    current.suspenseStartedAt = null;
  }
  current.finished = true;

  const totalMs = now() - current.startedAt;
  const startToSuspenseMs =
    current.suspenseDurationMs > 0
      ? Math.max(0, totalMs - current.suspenseDurationMs)
      : totalMs;

  if (typeof console !== 'undefined') {
    console.info(
      '[route-timing]',
      JSON.stringify({
        path: current.path,
        totalMs: Math.round(totalMs),
        startToSuspenseMs: Math.round(startToSuspenseMs),
        suspenseMs: Math.round(current.suspenseDurationMs),
        prefetched: current.prefetched,
        cacheHit: current.cacheHit,
      }),
    );
  }

  try {
    trackRouteTiming({
      path: current.path,
      totalMs,
      startToSuspenseMs,
      suspenseMs: current.suspenseDurationMs,
      prefetched: current.prefetched,
      cacheHit: current.cacheHit,
    });
  } catch {
    /* never throw from instrumentation */
  }
}
